<template>
  <div>
    <v-tooltip bottom>
      <template #activator="{ on, attrs }">
        <div v-bind="attrs" v-on="on">
          <v-autocomplete
            v-model="selectedPeople"
            :items="allPeople"
            :label="label"
            :loading="
              $apollo.queries.people.loading ||
              $apollo.queries.peopleQueried.loading
            "
            :search-input.sync="search"
            v-bind="[$attrs]"
            :class="[selectMixinClass, '']"
            no-filter
            item-text="name"
            item-value="id"
            :return-object="returnObject"
            :multiple="multiple"
            append-icon="$iconSearch"
            hide-no-data
            :clearable="clearable"
            outlined
            :chips="chips"
            :deletable-chips="chips"
            :data-test="dataTest"
            :menu-props="menuProperties"
            :hide-details="isFilter || hideDetails"
            @change="
              [
                inputVisibility($event, 'change'),
                emitReturnableObject ? $emit('returned-object', $event) : false
              ]
            "
            @focus="focused"
            @blur="inputVisibility"
          >
            <template #item="{ item }">
              <template v-if="item.loadMoreButton">
                <v-list-item-content
                  class="py-1"
                  @click.prevent="
                    $apollo.queries.people.loading ? null : fetchMore()
                  "
                >
                  <template v-if="$apollo.queries.people.loading">
                    <v-progress-circular indeterminate />
                  </template>
                  <template v-else>
                    {{ $t("common:loadMore.button") }}
                  </template>
                </v-list-item-content>
              </template>
              <template v-else>
                <v-list-item-content class="py-1">
                  <AppShowPersonWithDetails
                    :person="item"
                    :details-to-show="selectionDetailsToShow"
                  />
                </v-list-item-content>
              </template>
            </template>
            <template v-if="!chips" #selection="{ index, item }">
              <AppShowPersonName :person="item" no-link /><span
                v-if="multiple && index != selectedPeople.length - 1"
                class="mr-1"
                >,</span
              >
            </template>
          </v-autocomplete>
        </div>
      </template>
      <span>{{
        $t(
          "common:filters.search.tooltip.FilterWithTyping",
          "Please start typing a name (min 3 characters)",
          "Tooltip for filters, when user should start typing the name"
        )
      }}</span>
    </v-tooltip>
  </div>
</template>

<script>
import gql from "fraql";
import AppShowPersonWithDetails from "@/components/elements/AppShows/person/AppShowPersonWithDetails.vue";
import AppShowPersonName from "@/components/elements/AppShows/person/AppShowPersonName.vue";
import selectsMixin from "./selects_mixin";
import { uniq } from "lodash-es";

export default {
  name: "AppSelectPeople",
  components: {
    AppShowPersonWithDetails,
    AppShowPersonName
  },
  mixins: [selectsMixin],
  props: {
    value: {
      type: [String, Array, Object], // Single person ID or List of person ID-s
      default: null
    },
    selectionDetailsToShow: {
      type: Array,
      default: undefined
    },
    profQualificationIds: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    profQualificationGroupIds: {
      type: Array,
      required: false,
      default() {
        return [];
      }
    },
    additionalQueryParams: {
      type: Object,
      default: () => ({})
    },
    emitReturnableObject: {
      type: Boolean,
      default: false
    },
    queryWithFormData: {
      type: Boolean,
      default: false
    },
    hasActiveIdentifiers: { type: Boolean, default: null },
    label: {
      type: String,
      required: false,
      default: "People"
    },
    readonly: { type: Boolean, default: false },
    dataTest: {
      type: String,
      default: "selectPeople"
    },
    multiple: {
      type: Boolean,
      default: false
    },
    returnObject: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    fragmentsForQuery: {
      type: Object,
      default: () => undefined
    },
    chips: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      initialPersonIds: [],
      people: [],
      peopleQueried: [],
      menuProperties: {
        contentClass: "entsys--item-space",
        nudgeBottom: 4,
        nudgeRight: 8,
        maxHeight: "480px",
        closeOnContentClick: true,
        auto: true
      },
      search: "",
      moreToLoad: false
    };
  },
  computed: {
    selectedPeople: {
      get() {
        return this.value;
      },
      set(v) {
        this.$emit("input", v);
      }
    },
    allPeople() {
      let people = [...this.peopleQueried, ...this.people];
      if (this.returnObject && this.selectedPeople) {
        if (this.multiple) {
          people = [
            ...this.selectedPeople,
            ...this.peopleQueried,
            ...this.people
          ];
        } else {
          people.unshift(this.selectedPeople);
        }
      }
      if (this.moreToLoad) {
        people.push({
          divider: true,
          loadMoreButton: true
        });
      }
      return uniq(people);
    }
  },
  created() {
    if (this.selectedPeople && !this.returnObject) {
      if (this.multiple) {
        this.initialPersonIds = this.selectedPeople;
      } else {
        this.initialPersonIds = [this.selectedPeople];
      }
    }
  },
  fragments: {
    personFields: gql`
      fragment _ on Person {
        id
        ${AppShowPersonWithDetails.fragments.person}
      }
    `,
    formData: gql`
      fragment _ on Person {
        address {
          id
          addressText
          addressIndex
          addressCity
          addressHouse
          addressCountryId
        }
        registryCountryId
        feiid
        email
        phone
      }
    `
  },
  apollo: {
    peopleQueried: {
      query: function () {
        return gql`
         query people( $personIds: [ID]) {
           people (personIds: $personIds) {
              id
              ${this.fragmentsForQuery ?? ""}
              ${AppShowPersonWithDetails.fragments.person}
              ${this.queryWithFormData ? this.$options.fragments.formData : ""}
            }
         }
        `;
      },
      skip() {
        return this.initialPersonIds.length == 0;
      },
      variables() {
        return {
          personIds: this.initialPersonIds
        };
      },
      update({ people }) {
        return people;
      },
      fetchPolicy: "cache-first"
    },
    people: {
      query: function () {
        return gql`
          query appSelectPeople(
            $offset: Int
            $limit: Int
            $keyword: String
            $sortBy: String
            $sortDesc: Boolean
            $profQualificationIds: [ID]
            $profQualificationGroupIds: [ID]
            $hasActiveIdentifiers: Boolean
            $canJudgeCompetitionId: ID
            $profQualificationCheckDate: Date
            $applicableForRoleIds: [ID!]
            $applicableForRoleIdsCheckDate: Date
            $hasRegistrationsInEventIds: [ID]
          ) {
            people(
              offset: $offset
              limit: $limit
              keyword: $keyword
              sortBy: $sortBy
              sortDesc: $sortDesc
              profQualificationIds: $profQualificationIds
              profQualificationGroupIds: $profQualificationGroupIds
              hasActiveIdentifiers: $hasActiveIdentifiers
              canJudgeCompetitionId: $canJudgeCompetitionId
              profQualificationCheckDate: $profQualificationCheckDate
              applicableForRoleIds: $applicableForRoleIds
              applicableForRoleIdsCheckDate: $applicableForRoleIdsCheckDate
              hasRegistrationsInEventIds: $hasRegistrationsInEventIds
            ) {
              id
              ${AppShowPersonWithDetails.fragments.person}
              ${this.fragmentsForQuery ?? ""}
              ${this.queryWithFormData ? this.$options.fragments.formData : ""}
            }
          }
        `;
      },
      debounce: 750,
      variables() {
        return {
          offset: 0,
          limit: 50,
          sortBy: "name",
          sortDesc: false,
          keyword: this.search,
          profQualificationIds: this.profQualificationIds,
          profQualificationGroupIds: this.profQualificationGroupIds,
          hasActiveIdentifiers: this.hasActiveIdentifiers,
          ...this.additionalQueryParams
        };
      },
      skip() {
        return !this.search || this.search.length < 3;
      },
      result({ loading, data }) {
        if (loading) return false;
        this.moreToLoad = data.people?.length >= 200;
      },
      fetchPolicy: "cache-and-network"
    }
  },
  methods: {
    fetchMore() {
      this.$apollo.queries.people.fetchMore({
        variables: {
          offset: this.people.length
        }
      });
    }
  }
};
</script>
